import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { Link as RouterLink } from 'react-router-dom';
import { functions } from '../../firebase';
import {
  FRICTION_ANALYZE_RANGE_OPTIONS,
  FRICTION_SUGGESTION_TYPES,
  FRICTION_TAGS_FOR_ANALYZE,
  buildRosterFrictionDeepLink,
  frictionPresetToDateRange,
  formatFrictionEvidenceChip,
} from '../../lib/frictionSuggestions';

const analyzeFrictionSuggestions = httpsCallable(
  functions,
  'analyzeFrictionSuggestions',
);
const listFrictionSuggestions = httpsCallable(
  functions,
  'listFrictionSuggestions',
);
const reviewFrictionSuggestion = httpsCallable(
  functions,
  'reviewFrictionSuggestion',
);

const DEFAULT_PRESET = 60;
const DEFAULT_RANGE = frictionPresetToDateRange(DEFAULT_PRESET);

/**
 * Reports Hub — on-demand friction Analyze + review inbox.
 * Approve never auto-applies prompts or platform code.
 */
export default function FrictionInsightsPanel() {
  const [frictionTag, setFrictionTag] = useState('patch_content_omission_loop');
  const [assigneeAgentRole, setAssigneeAgentRole] = useState('');
  const [suggestionType, setSuggestionType] = useState('');
  const [rangePreset, setRangePreset] = useState(
    /** @type {number | 'custom'} */ (DEFAULT_PRESET),
  );
  const [startDate, setStartDate] = useState(DEFAULT_RANGE.start);
  const [endDate, setEndDate] = useState(DEFAULT_RANGE.end);
  const [platformBugScan, setPlatformBugScan] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [gateResult, setGateResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [briefingCoverage, setBriefingCoverage] = useState(null);
  const [reviewBusyId, setReviewBusyId] = useState('');
  const [listStatus, setListStatus] = useState('pending_review');
  const [rejectDialog, setRejectDialog] = useState(
    /** @type {{ suggestionId: string } | null} */ (null),
  );
  const [rejectReasonDraft, setRejectReasonDraft] = useState('');

  const applyPreset = (days) => {
    const next = frictionPresetToDateRange(days);
    setRangePreset(days);
    setStartDate(next.start);
    setEndDate(next.end);
  };

  const handleStartChange = (value) => {
    setStartDate(value);
    setRangePreset('custom');
  };

  const handleEndChange = (value) => {
    setEndDate(value);
    setRangePreset('custom');
  };

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      const result = await listFrictionSuggestions({ status: listStatus });
      const data = result?.data || {};
      setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
      setBriefingCoverage(
        data.briefingCoverage && typeof data.briefingCoverage === 'object'
          ? data.briefingCoverage
          : null,
      );
    } catch (e) {
      console.error('FrictionInsightsPanel list:', e);
      setError(
        e?.message ||
          'Could not load friction suggestions.',
      );
    } finally {
      setLoadingList(false);
    }
  }, [listStatus]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError('');
    setInfo('');
    setGateResult(null);
    try {
      const result = await analyzeFrictionSuggestions({
        frictionTag,
        assigneeAgentRole: assigneeAgentRole.trim() || undefined,
        suggestionType: platformBugScan
          ? 'platform_bug_flag'
          : suggestionType || undefined,
        rangeDays: rangePreset === 'custom' ? undefined : rangePreset,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        platformBugScan,
        forceRefresh,
      });
      const data = result?.data || {};
      if (!data.ok && data.error === 'not_enough_evidence') {
        setGateResult(data);
        setInfo(
          'Not enough evidence for an LLM draft. Counts and ticket IDs are shown below — no suggestion was generated.',
        );
        return;
      }
      if (!data.ok) {
        setError(data.message || data.error || 'Analyze failed.');
        if (data.gate) setGateResult(data);
        return;
      }
      setInfo(
        data.cached
          ? 'Reused cached suggestion for this unchanged slice (no new LLM call).'
          : 'Draft suggestion ready for human review. Directional only — verify tickets before editing prompts.',
      );
      setGateResult(data.gate ? { gate: data.gate, ok: true } : null);
      await refreshList();
    } catch (e) {
      console.error('FrictionInsightsPanel analyze:', e);
      setError(
        e?.details ||
          e?.message ||
          'Analyze failed. Check role, billing, and rate limits.',
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReview = async (suggestionId, action, reason) => {
    setReviewBusyId(suggestionId);
    setError('');
    setInfo('');
    try {
      const payload = { suggestionId, action };
      if (typeof reason === 'string' && reason.trim()) {
        payload.reason = reason.trim().slice(0, 500);
      }
      const result = await reviewFrictionSuggestion(payload);
      const data = result?.data || {};
      if (data.wroteAiAgents === true) {
        setError(
          'Invariant violated: review wrote ai_agents. Stop and investigate.',
        );
        return;
      }
      if (action === 'approve_manual' && data.rosterDeepLink) {
        const href = buildRosterFrictionDeepLink(data.rosterDeepLink);
        setInfo(
          `Approved for manual apply only. Open the roster editor to prefill — Save is still required: ${href}`,
        );
      } else if (action === 'approve_flag' && data.linkedOpsTicketId) {
        setInfo(
          `Platform flag approved. Ops ticket ${data.linkedOpsTicketId} created for Cursor investigation (no code changes).`,
        );
      } else if (action === 'reject') {
        setInfo(
          data.rejectionReason
            ? `Rejected with reason saved on the suggestion.`
            : `Marked rejected.`,
        );
      } else {
        setInfo(`Marked ${data.status || action}.`);
      }
      await refreshList();
    } catch (e) {
      console.error('FrictionInsightsPanel review:', e);
      setError(e?.message || 'Review action failed.');
    } finally {
      setReviewBusyId('');
    }
  };

  const openRejectDialog = (suggestionId) => {
    setRejectReasonDraft('');
    setRejectDialog({ suggestionId });
  };

  const closeRejectDialog = () => {
    if (reviewBusyId) return;
    setRejectDialog(null);
    setRejectReasonDraft('');
  };

  const confirmReject = async () => {
    if (!rejectDialog?.suggestionId) return;
    const id = rejectDialog.suggestionId;
    await handleReview(id, 'reject', rejectReasonDraft);
    setRejectDialog(null);
    setRejectReasonDraft('');
  };

  return (
    <Box
      sx={{
        mx: 2,
        mt: 1,
        mb: 1,
        p: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        flexShrink: 0,
        maxHeight: { md: 420 },
        overflow: 'auto',
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Friction insights
          </Typography>
          <Typography variant="caption" color="text.secondary">
            On-demand Analyze only. Suggestions are directional — verify cited
            tickets before editing agent prompts. Approve never auto-applies.
          </Typography>
        </Box>

        {briefingCoverage?.incomplete && (
          <Alert severity="warning">
            Briefing coverage is incomplete for this workspace: analysis can
            only see{' '}
            <strong>
              {briefingCoverage.withBriefing} of {briefingCoverage.terminalSampled}
            </strong>{' '}
            sampled resolved/closed tickets (some recent tickets may be missing
            Phase 1 briefings). Treat patterns as directional until coverage
            catches up.
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'flex-end' }}
          flexWrap="wrap"
        >
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="friction-tag-label">Friction tag</InputLabel>
            <Select
              labelId="friction-tag-label"
              label="Friction tag"
              value={frictionTag}
              onChange={(e) => setFrictionTag(e.target.value)}
            >
              {FRICTION_TAGS_FOR_ANALYZE.map((tag) => (
                <MenuItem key={tag} value={tag}>
                  {tag}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Agent role (optional)"
            value={assigneeAgentRole}
            onChange={(e) => setAssigneeAgentRole(e.target.value)}
            placeholder="e.g. Backend Engineer"
            sx={{ minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }} disabled={platformBugScan}>
            <InputLabel id="suggestion-type-label">Type preference</InputLabel>
            <Select
              labelId="suggestion-type-label"
              label="Type preference"
              value={suggestionType}
              onChange={(e) => setSuggestionType(e.target.value)}
            >
              <MenuItem value="">Auto</MenuItem>
              {FRICTION_SUGGESTION_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="range-preset-label">Preset</InputLabel>
            <Select
              labelId="range-preset-label"
              label="Preset"
              value={rangePreset}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'custom') {
                  setRangePreset('custom');
                  return;
                }
                applyPreset(Number(v));
              }}
            >
              {FRICTION_ANALYZE_RANGE_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  Last {d} days
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Start"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => handleStartChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="End"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => handleEndChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={platformBugScan}
                onChange={(e) => setPlatformBugScan(e.target.checked)}
              />
            }
            label="Platform-bug scan"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={forceRefresh}
                onChange={(e) => setForceRefresh(e.target.checked)}
              />
            }
            label="Force refresh"
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleAnalyze}
            disabled={analyzing || !frictionTag}
          >
            {analyzing ? 'Analyzing…' : 'Analyze friction'}
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {info && (
          <Alert severity="info" onClose={() => setInfo('')}>
            {info}
          </Alert>
        )}

        {gateResult && !gateResult.ok && gateResult.error === 'not_enough_evidence' && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight={600}>
              Not enough evidence (tunable gates)
            </Typography>
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              tickets={gateResult.gate?.ticketsInSlice ?? '—'} (min{' '}
              {gateResult.gate?.minTicketsInSlice}), tagHits=
              {gateResult.gate?.tagHits ?? '—'} (min {gateResult.gate?.minTagHits}
              ), share=
              {typeof gateResult.gate?.share === 'number'
                ? `${(gateResult.gate.share * 100).toFixed(1)}%`
                : '—'}
            </Typography>
            {(gateResult.gate?.reasons || []).map((r) => (
              <Typography key={r} variant="caption" display="block">
                • {r}
              </Typography>
            ))}
            {Array.isArray(gateResult.evidenceTicketIds) &&
              gateResult.evidenceTicketIds.length > 0 && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Ticket IDs: {gateResult.evidenceTicketIds.join(', ')}
                </Typography>
              )}
          </Alert>
        )}

        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Review inbox
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={listStatus}
            onChange={(_e, next) => {
              if (next) setListStatus(next);
            }}
          >
            <ToggleButton value="pending_review">Pending</ToggleButton>
            <ToggleButton value="rejected">Rejected</ToggleButton>
          </ToggleButtonGroup>
          {loadingList && <CircularProgress size={16} />}
          <Button size="small" onClick={refreshList} disabled={loadingList}>
            Refresh
          </Button>
        </Stack>

        {!loadingList && suggestions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {listStatus === 'rejected'
              ? 'No rejected suggestions.'
              : 'No pending suggestions.'}
          </Typography>
        )}

        {suggestions.map((s) => {
          const busy = reviewBusyId === s.id;
          const isRejected = s.status === 'rejected';
          const storedReason =
            (typeof s.rejectionReason === 'string' &&
              s.rejectionReason.trim()) ||
            (typeof s.review?.reason === 'string' && s.review.reason.trim()) ||
            '';
          const deepLink = buildRosterFrictionDeepLink({
            frictionSuggestionId: s.id,
            agentRole: s.slice?.assigneeAgentRole || '',
          });
          return (
            <Box
              key={s.id}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={s.type || 'unknown'} />
                  <Chip size="small" label={`confidence: ${s.confidence || '—'}`} />
                  {s.exploratory && (
                    <Chip size="small" color="warning" label="extra-exploratory" />
                  )}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={
                      (s.evidenceCount ?? (s.evidenceTicketIds || []).length) <
                        Math.min(
                          5,
                          Number(s.matchingTicketCount) ||
                            Number(s.slice?.tagHits) ||
                            0,
                        )
                        ? 'warning'
                        : 'default'
                    }
                    label={formatFrictionEvidenceChip(s)}
                  />
                  {s.citationsExpanded && (
                    <Chip
                      size="small"
                      color="warning"
                      label="citations auto-expanded"
                    />
                  )}
                </Stack>
                <Typography variant="subtitle2">{s.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {s.summary}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Proposed: {s.proposedAction || '—'}
                </Typography>
                <Typography variant="caption" display="block">
                  Evidence tickets:{' '}
                  {(s.evidenceTicketIds || []).map((tid, idx) => (
                    <React.Fragment key={tid}>
                      {idx > 0 ? ', ' : ''}
                      <Link
                        component={RouterLink}
                        to={`/dashboard/tickets/${tid}`}
                        underline="hover"
                      >
                        {tid}
                      </Link>
                    </React.Fragment>
                  ))}
                </Typography>
                {(s.caveats || []).length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Caveats: {(s.caveats || []).join(' · ')}
                  </Typography>
                )}
                {(s.doesNotProve || []).length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Does not prove: {(s.doesNotProve || []).join(' · ')}
                  </Typography>
                )}
                {isRejected && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    <Typography variant="caption" display="block">
                      Rejection reason:{' '}
                      {storedReason || '(none provided)'}
                    </Typography>
                  </Alert>
                )}
                {!isRejected && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    disabled={busy}
                    onClick={() => openRejectDialog(s.id)}
                  >
                    Reject
                  </Button>
                  <Button
                    size="small"
                    disabled={busy}
                    onClick={() => handleReview(s.id, 'needs_evidence')}
                  >
                    Needs evidence
                  </Button>
                  {s.type === 'platform_bug_flag' ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      disabled={busy}
                      onClick={() => handleReview(s.id, 'approve_flag')}
                    >
                      Approve platform flag
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busy}
                      onClick={async () => {
                        await handleReview(s.id, 'approve_manual');
                      }}
                    >
                      Approve for manual apply
                    </Button>
                  )}
                  {s.type === 'boot_prompt_direction' && (
                    <Button
                      size="small"
                      component={RouterLink}
                      to={deepLink}
                      disabled={busy}
                      onClick={async (e) => {
                        // Ensure approved_manual before editor prefill / appliedAt.
                        e.preventDefault();
                        setReviewBusyId(s.id);
                        try {
                          const result = await reviewFrictionSuggestion({
                            suggestionId: s.id,
                            action: 'approve_manual',
                          });
                          if (result?.data?.wroteAiAgents === true) {
                            setError(
                              'Invariant violated: review wrote ai_agents.',
                            );
                            return;
                          }
                          window.location.assign(deepLink);
                        } catch (err) {
                          setError(err?.message || 'Approve failed.');
                        } finally {
                          setReviewBusyId('');
                          refreshList();
                        }
                      }}
                    >
                      Approve & open roster editor
                    </Button>
                  )}
                </Stack>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Dialog
        open={Boolean(rejectDialog)}
        onClose={closeRejectDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject suggestion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Optional — explain why this draft is being rejected. Saved on the
            suggestion document for later review.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Rejection reason (optional)"
            value={rejectReasonDraft}
            onChange={(e) => setRejectReasonDraft(e.target.value)}
            inputProps={{ maxLength: 500 }}
            helperText={`${rejectReasonDraft.length}/500`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeRejectDialog}
            disabled={Boolean(reviewBusyId)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmReject}
            disabled={Boolean(reviewBusyId)}
          >
            Confirm reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
